// ===================================================================
// KAIXO - Modal de verificación de usuario
// ===================================================================
// Permite al usuario solicitar verificación en 3 niveles:
//   · basic    — identidad personal (gratis)
//   · creator  — creador de contenido ($7/mes)
//   · business — cuenta de negocio ($15/mes)
//
// Sube documentos a multipart y los envía a /api/verifications/submit.
// Si ya hay una solicitud en curso muestra su estado (pending,
// info_requested, approved, rejected).
// ===================================================================

import React, { useEffect, useState } from 'react';
import { verificationSubmit, verificationMine, verificationEligibility } from '../../services/adminApi';
import PlansModal from '../Plans/PlansModal';
import useModalClose from '../../hooks/useModalClose';
import friendlyError from '../../utils/friendlyError';
import './VerificationModal.css';

// Diccionario i18n local — los 7 idiomas con más volumen + fallback a inglés.
const I18N = {
    español: {
        title: 'Verifícate en KAIXO',
        verifyInProgress: 'Verificación en curso',
        alreadyVerified: 'Ya estás verificado',
        statusPending: 'En revisión — recibirás respuesta en 24-72h.',
        statusInfoRequested: 'Se requiere información adicional.',
        statusApproved: '¡Verificación aprobada!',
        statusRejected: 'Verificación rechazada.',
        statusExpired: 'Verificación expirada. Debes renovarla.',
        rejectedHeader: 'Solicitud anterior rechazada.',
        canResubmit: 'Puedes volver a enviarla corrigiendo los problemas.',
        levelRequested: 'Nivel solicitado',
        sentAt: 'Enviado',
        infoRequestedH: 'Información solicitada',
        cancelAndResubmit: 'Por favor, cancela esta solicitud y envía una nueva con la información pedida.',
        approvedAt: 'Aprobado',
        renewalAt: 'Renovación',
        wantUpgrade: '¿Quieres pasar a',
        evolveBtn: '↑ Evolucionar verificación',
        checkingReqs: 'Comprobando requisitos…',
        meetsReqs: '✓ Cumples los requisitos',
        notMeetsReqs: '⚠ No cumples los requisitos',
        legalDisclaimer: 'Tus documentos se revisan manualmente y se eliminan tras la decisión. No los compartimos con terceros ni entrenamos modelos con ellos.',
        cancel: 'Cancelar',
        submit: 'Enviar solicitud',
        submitting: 'Enviando…',
        notMeetsTitle: 'No cumples los requisitos mínimos para este nivel',
        fileTooBig: 'El archivo es muy grande (máx 10 MB).',
        errComplete: 'Completa nombre legal, número de documento y país.',
        errCompany: 'Completa los datos de la empresa.',
        errFutureDate: 'La fecha del extracto registral no puede ser futura.',
        errOldRegistry: 'El extracto registral debe tener menos de 30 días de antigüedad.',
        errMissingFile: 'Falta el archivo',
        errSocialMin: 'Añade al menos un enlace a tus redes sociales.',
        sendErrFallback: 'No se pudo enviar la solicitud',
        personalDataH: 'Datos personales',
        legalNamePh: 'Nombre legal completo',
        documentNumberPh: 'Número de documento',
        countryPh: 'País',
        companyDataH: 'Datos de la empresa',
        companyNamePh: 'Nombre legal de la empresa',
        taxIdPh: 'Identificación fiscal (NIF/RUC/Tax ID)',
        registryDateLabel: 'Fecha del extracto registral (máx 30 días)',
        personalIdentityH: 'Identidad personal',
        idFrontLabel: 'Documento de identidad (anverso)',
        idFrontHint: 'DNI, pasaporte, cédula, licencia.',
        idBackLabel: 'Documento de identidad (reverso)',
        idBackHint: 'Si el documento tiene reverso con datos.',
        selfieLabel: 'Selfie sujetando el documento',
        selfieHint: 'Cara + documento visibles en la misma foto.',
        socialLinksH: 'Enlaces a redes sociales',
        socialLinksHint: 'Al menos uno. Debe ser tu perfil público principal.',
        socialPlaceholder: 'https://instagram.com/tu-usuario',
        addLink: '+ Añadir enlace',
        addAnotherLink: '+ Añadir otro',
        companyDocsH: 'Documentos de la empresa',
        articlesLabel: 'Acta constitutiva / Escritura de constitución',
        articlesHint: 'Documento que acredita la creación legal de la empresa.',
        registryExtractLabel: 'Certificado del registro mercantil',
        registryExtractHint: 'Emitido en los últimos 30 días.',
        powerOfAttorneyLabel: 'Poderes del representante legal',
        powerOfAttorneyHint: 'Documento que te autoriza a representar a la empresa.'
    },
    inglés: {
        title: 'Verify on KAIXO',
        verifyInProgress: 'Verification in progress',
        alreadyVerified: 'You are already verified',
        statusPending: 'Under review — you will get a response within 24-72h.',
        statusInfoRequested: 'Additional information required.',
        statusApproved: 'Verification approved!',
        statusRejected: 'Verification rejected.',
        statusExpired: 'Verification expired. You need to renew.',
        rejectedHeader: 'Previous request rejected.',
        canResubmit: 'You can resubmit after fixing the issues.',
        levelRequested: 'Requested level',
        sentAt: 'Submitted',
        infoRequestedH: 'Information requested',
        cancelAndResubmit: 'Please cancel this request and submit a new one with the requested information.',
        approvedAt: 'Approved',
        renewalAt: 'Renewal',
        wantUpgrade: 'Want to upgrade to',
        evolveBtn: '↑ Upgrade verification',
        checkingReqs: 'Checking requirements…',
        meetsReqs: '✓ You meet the requirements',
        notMeetsReqs: '⚠ You do not meet the requirements',
        legalDisclaimer: 'Documents are reviewed manually and deleted after the decision. Never shared with third parties, never used to train models.',
        cancel: 'Cancel',
        submit: 'Submit request',
        submitting: 'Submitting…',
        notMeetsTitle: 'You do not meet the minimum requirements for this level',
        fileTooBig: 'File too big (max 10 MB).',
        errComplete: 'Fill in legal name, document number and country.',
        errCompany: 'Fill in the company details.',
        errFutureDate: 'Registry extract date cannot be in the future.',
        errOldRegistry: 'Registry extract must be less than 30 days old.',
        errMissingFile: 'Missing file',
        errSocialMin: 'Add at least one social media link.',
        sendErrFallback: 'Could not send request',
        personalDataH: 'Personal data',
        legalNamePh: 'Full legal name',
        documentNumberPh: 'Document number',
        countryPh: 'Country',
        companyDataH: 'Company data',
        companyNamePh: 'Full legal company name',
        taxIdPh: 'Tax ID (VAT/EIN/CIF...)',
        registryDateLabel: 'Registry extract date (max 30 days old)',
        personalIdentityH: 'Personal identity',
        idFrontLabel: 'ID document (front)',
        idFrontHint: 'Passport, national ID, driving licence.',
        idBackLabel: 'ID document (back)',
        idBackHint: 'If the document has data on the back.',
        selfieLabel: 'Selfie holding the document',
        selfieHint: 'Face + document visible in the same photo.',
        socialLinksH: 'Social links',
        socialLinksHint: 'At least one. Must be your main public profile.',
        socialPlaceholder: 'https://instagram.com/your-handle',
        addLink: '+ Add link',
        addAnotherLink: '+ Add another',
        companyDocsH: 'Company documents',
        articlesLabel: 'Articles of incorporation',
        articlesHint: 'Document proving the legal creation of the company.',
        registryExtractLabel: 'Commercial registry certificate',
        registryExtractHint: 'Issued within the last 30 days.',
        powerOfAttorneyLabel: 'Power of attorney',
        powerOfAttorneyHint: 'Document authorising you to represent the company.'
    },
    francés: {
        title: 'Vérifie-toi sur KAIXO',
        verifyInProgress: 'Vérification en cours',
        alreadyVerified: 'Tu es déjà vérifié',
        statusPending: 'En examen — réponse dans 24-72h.',
        statusInfoRequested: 'Informations additionnelles requises.',
        statusApproved: 'Vérification approuvée !',
        statusRejected: 'Vérification refusée.',
        statusExpired: 'Vérification expirée. Renouvelle-la.',
        rejectedHeader: 'Demande précédente refusée.',
        canResubmit: 'Tu peux ré-envoyer en corrigeant les problèmes.',
        levelRequested: 'Niveau demandé',
        sentAt: 'Envoyé',
        infoRequestedH: 'Informations demandées',
        cancelAndResubmit: 'Annule cette demande et envoie-en une nouvelle avec les infos requises.',
        approvedAt: 'Approuvé',
        renewalAt: 'Renouvellement',
        wantUpgrade: 'Passer à',
        evolveBtn: '↑ Faire évoluer la vérification',
        checkingReqs: 'Vérification des prérequis…',
        meetsReqs: '✓ Tu remplis les prérequis',
        notMeetsReqs: '⚠ Tu ne remplis pas les prérequis',
        legalDisclaimer: 'Les documents sont examinés manuellement et supprimés après la décision. Jamais partagés ni utilisés pour entraîner des modèles.',
        cancel: 'Annuler',
        submit: 'Envoyer la demande',
        submitting: 'Envoi…',
        notMeetsTitle: 'Tu ne remplis pas les prérequis minimaux pour ce niveau',
        fileTooBig: 'Fichier trop volumineux (max 10 Mo).',
        errComplete: 'Remplis nom légal, numéro de document et pays.',
        errCompany: 'Remplis les infos de l\'entreprise.',
        errFutureDate: 'La date de l\'extrait ne peut pas être dans le futur.',
        errOldRegistry: 'L\'extrait registral doit avoir moins de 30 jours.',
        errMissingFile: 'Fichier manquant',
        errSocialMin: 'Ajoute au moins un lien de réseau social.',
        sendErrFallback: 'Impossible d\'envoyer la demande'
    },
    alemán: {
        title: 'Verifiziere dich auf KAIXO',
        verifyInProgress: 'Verifizierung läuft',
        alreadyVerified: 'Du bist bereits verifiziert',
        statusPending: 'In Prüfung — Antwort in 24-72h.',
        statusInfoRequested: 'Weitere Informationen erforderlich.',
        statusApproved: 'Verifizierung genehmigt!',
        statusRejected: 'Verifizierung abgelehnt.',
        statusExpired: 'Verifizierung abgelaufen. Erneuere sie.',
        rejectedHeader: 'Vorherige Anfrage abgelehnt.',
        canResubmit: 'Du kannst sie nach Korrektur erneut einreichen.',
        levelRequested: 'Beantragtes Level',
        sentAt: 'Gesendet',
        infoRequestedH: 'Angeforderte Informationen',
        cancelAndResubmit: 'Bitte storniere diese Anfrage und sende eine neue mit den geforderten Infos.',
        approvedAt: 'Genehmigt',
        renewalAt: 'Verlängerung',
        wantUpgrade: 'Wechseln zu',
        evolveBtn: '↑ Verifizierung upgraden',
        checkingReqs: 'Anforderungen werden geprüft…',
        meetsReqs: '✓ Du erfüllst die Anforderungen',
        notMeetsReqs: '⚠ Du erfüllst die Anforderungen nicht',
        legalDisclaimer: 'Dokumente werden manuell geprüft und nach der Entscheidung gelöscht. Niemals geteilt, niemals für Modelltraining genutzt.',
        cancel: 'Abbrechen',
        submit: 'Anfrage senden',
        submitting: 'Sende…',
        notMeetsTitle: 'Du erfüllst die Mindestanforderungen für dieses Level nicht',
        fileTooBig: 'Datei zu groß (max 10 MB).',
        errComplete: 'Fülle Name, Dokumentnummer und Land aus.',
        errCompany: 'Fülle die Unternehmensdaten aus.',
        errFutureDate: 'Das Datum des Auszugs darf nicht in der Zukunft liegen.',
        errOldRegistry: 'Der Handelsregisterauszug muss jünger als 30 Tage sein.',
        errMissingFile: 'Fehlende Datei',
        errSocialMin: 'Füge mindestens einen Social-Media-Link hinzu.',
        sendErrFallback: 'Anfrage konnte nicht gesendet werden'
    },
    portugués: {
        title: 'Verifica-te em KAIXO',
        verifyInProgress: 'Verificação em curso',
        alreadyVerified: 'Já estás verificado',
        statusPending: 'Em análise — resposta em 24-72h.',
        statusInfoRequested: 'Necessária informação adicional.',
        statusApproved: 'Verificação aprovada!',
        statusRejected: 'Verificação recusada.',
        statusExpired: 'Verificação expirada. Tens de renovar.',
        rejectedHeader: 'Pedido anterior recusado.',
        canResubmit: 'Podes reenviar corrigindo os problemas.',
        levelRequested: 'Nível solicitado',
        sentAt: 'Enviado',
        infoRequestedH: 'Informação solicitada',
        cancelAndResubmit: 'Cancela este pedido e envia um novo com a informação pedida.',
        approvedAt: 'Aprovado',
        renewalAt: 'Renovação',
        wantUpgrade: 'Passar a',
        evolveBtn: '↑ Evoluir verificação',
        checkingReqs: 'A verificar requisitos…',
        meetsReqs: '✓ Cumpres os requisitos',
        notMeetsReqs: '⚠ Não cumpres os requisitos',
        legalDisclaimer: 'Os documentos são revistos manualmente e eliminados após decisão. Nunca partilhados, nunca usados para treinar modelos.',
        cancel: 'Cancelar',
        submit: 'Enviar pedido',
        submitting: 'A enviar…',
        notMeetsTitle: 'Não cumpres os requisitos mínimos para este nível',
        fileTooBig: 'Ficheiro demasiado grande (máx 10 MB).',
        errComplete: 'Preenche nome legal, número de documento e país.',
        errCompany: 'Preenche os dados da empresa.',
        errFutureDate: 'A data do extracto não pode ser no futuro.',
        errOldRegistry: 'O extracto registral deve ter menos de 30 dias.',
        errMissingFile: 'Ficheiro em falta',
        errSocialMin: 'Adiciona pelo menos um link de rede social.',
        sendErrFallback: 'Não foi possível enviar o pedido'
    },
    italiano: {
        title: 'Verifica il tuo profilo su KAIXO',
        verifyInProgress: 'Verifica in corso',
        alreadyVerified: 'Sei già verificato',
        statusPending: 'In revisione — risposta in 24-72h.',
        statusInfoRequested: 'Servono informazioni aggiuntive.',
        statusApproved: 'Verifica approvata!',
        statusRejected: 'Verifica rifiutata.',
        statusExpired: 'Verifica scaduta. Devi rinnovarla.',
        rejectedHeader: 'Richiesta precedente rifiutata.',
        canResubmit: 'Puoi rinviarla correggendo i problemi.',
        levelRequested: 'Livello richiesto',
        sentAt: 'Inviato',
        infoRequestedH: 'Informazioni richieste',
        cancelAndResubmit: 'Annulla questa richiesta e inviane una nuova con le info richieste.',
        approvedAt: 'Approvato',
        renewalAt: 'Rinnovo',
        wantUpgrade: 'Passare a',
        evolveBtn: '↑ Aggiorna verifica',
        checkingReqs: 'Controllo requisiti…',
        meetsReqs: '✓ Soddisfi i requisiti',
        notMeetsReqs: '⚠ Non soddisfi i requisiti',
        legalDisclaimer: 'I documenti sono revisionati manualmente ed eliminati dopo la decisione. Mai condivisi, mai usati per addestrare modelli.',
        cancel: 'Annulla',
        submit: 'Invia richiesta',
        submitting: 'Invio…',
        notMeetsTitle: 'Non soddisfi i requisiti minimi per questo livello',
        fileTooBig: 'File troppo grande (max 10 MB).',
        errComplete: 'Compila nome legale, documento e paese.',
        errCompany: 'Compila i dati aziendali.',
        errFutureDate: 'La data dell\'estratto non può essere futura.',
        errOldRegistry: 'L\'estratto registrale deve essere di meno di 30 giorni.',
        errMissingFile: 'File mancante',
        errSocialMin: 'Aggiungi almeno un link social.',
        sendErrFallback: 'Impossibile inviare la richiesta'
    },
    euskera: {
        title: 'Egiaztatu KAIXOn',
        verifyInProgress: 'Egiaztapena martxan',
        alreadyVerified: 'Egiaztatuta zaude jada',
        statusPending: 'Berrikusten — erantzuna 24-72h-tan.',
        statusInfoRequested: 'Informazio gehigarria behar da.',
        statusApproved: 'Egiaztapena onartuta!',
        statusRejected: 'Egiaztapena baztertuta.',
        statusExpired: 'Egiaztapena iraungita. Berritu behar duzu.',
        rejectedHeader: 'Aurreko eskaera baztertuta.',
        canResubmit: 'Arazoak konpondu eta berriz bidali dezakezu.',
        levelRequested: 'Eskatutako maila',
        sentAt: 'Bidalita',
        infoRequestedH: 'Eskatutako informazioa',
        cancelAndResubmit: 'Mesedez, ezeztatu eskaera eta bidali berri bat eskatutako informazioarekin.',
        approvedAt: 'Onartuta',
        renewalAt: 'Berriztatzea',
        wantUpgrade: 'Pasatu nahi duzu',
        evolveBtn: '↑ Egiaztapena bilakatu',
        checkingReqs: 'Eskakizunak egiaztatzen…',
        meetsReqs: '✓ Eskakizunak betetzen dituzu',
        notMeetsReqs: '⚠ Ez dituzu eskakizunak betetzen',
        legalDisclaimer: 'Dokumentuak eskuz berrikusten dira eta erabakiaren ondoren ezabatu. Ez dira inoiz partekatzen, ez ereduak entrenatzeko erabiltzen.',
        cancel: 'Utzi',
        submit: 'Eskaera bidali',
        submitting: 'Bidaltzen…',
        notMeetsTitle: 'Maila honetarako gutxieneko eskakizunak ez dituzu betetzen',
        fileTooBig: 'Fitxategia handiegia da (geh. 10 MB).',
        errComplete: 'Bete izen legala, dokumentu zenbakia eta herrialdea.',
        errCompany: 'Bete enpresaren datuak.',
        errFutureDate: 'Erregistro-laburpenaren data ezin da etorkizunekoa izan.',
        errOldRegistry: 'Erregistro-laburpena 30 egun baino gutxiagokoa izan behar da.',
        errMissingFile: 'Fitxategia falta da',
        errSocialMin: 'Gehitu sare sozialeko esteka bat gutxienez.',
        sendErrFallback: 'Eskaera ezin izan da bidali'
    }
};
function pickL() {
    try {
        const u = JSON.parse(localStorage.getItem('kaixo_user') || '{}');
        const primary = I18N[u?.language] || I18N.inglés;
        const fallback = I18N.inglés;
        // Proxy con fallback por-clave: si el bloque de idioma del user no
        // tiene esa key (ej. añadida después), cae al inglés en vez de
        // devolver undefined que rompería el render.
        return new Proxy(primary, {
            get(target, prop) {
                return target[prop] !== undefined ? target[prop] : fallback[prop];
            }
        });
    } catch { return I18N.inglés; }
}

const LEVEL_INFO = {
    basic: {
        label: 'Identidad verificada',
        icon: '🟢',
        price: 'Gratis',
        subtitle: 'Confirma que eres una persona real. Tarjeta verde en tu perfil.',
        required: ['idFront', 'idBack', 'selfie']
    },
    creator: {
        label: 'Creador verificado',
        icon: '🟣',
        price: '$7/mes',
        subtitle: 'Para creadores con audiencia. Incluye beneficios en Latidos y Mundo.',
        required: ['idFront', 'idBack', 'selfie', 'socialLinks']
    },
    business: {
        label: 'Negocio verificado',
        icon: '🔵',
        price: '$15/mes',
        subtitle: 'Empresas y negocios. Requiere documentación legal.',
        required: ['idFront', 'idBack', 'selfie', 'articles', 'registryExtract', 'powerOfAttorney']
    }
};

const STATUS_COLORS = {
    pending: '#f59e0b', info_requested: '#3b82f6', approved: '#22c55e',
    rejected: '#ef4444', expired: '#888'
};

function getStatusInfo(status, L) {
    const colorMap = STATUS_COLORS;
    const textMap = {
        pending: L.statusPending,
        info_requested: L.statusInfoRequested,
        approved: L.statusApproved,
        rejected: L.statusRejected,
        expired: L.statusExpired
    };
    return { color: colorMap[status] || '#888', text: textMap[status] || '' };
}

export default function VerificationModal({ currentUser, channelId, channelName, channelVerifiedLevel, onClose }) {
    const L = pickL();
    const [mineLoading, setMineLoading] = useState(true);
    const [current, setCurrent] = useState(null);
    // Pre-selección del nivel:
    // 1. Si tenemos channelVerifiedLevel (canal-específico): siguiente nivel.
    // 2. Si no, fallback a currentUser.verifiedLevel (compat legacy).
    // 3. Si no hay nada: basic.
    const initialLevel = (() => {
        const cur = channelVerifiedLevel || currentUser?.verifiedLevel;
        if (cur === 'basic') return 'creator';
        if (cur === 'creator') return 'business';
        return 'basic';
    })();
    const [level, setLevel] = useState(initialLevel);
    const [wantsUpgrade, setWantsUpgrade] = useState(false);
    const [showPayments, setShowPayments] = useState(false);
    const [declared, setDeclared] = useState({
        legalName: '',
        documentNumber: '',
        country: '',
        companyName: '',
        taxId: '',
        registryDate: ''
    });
    const [files, setFiles] = useState({
        idFront: null, idBack: null, selfie: null,
        articles: null, registryExtract: null, powerOfAttorney: null
    });
    const [socialLinks, setSocialLinks] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    // Eligibility según el nivel seleccionado: requirements + cumple/no cumple
    const [eligibility, setEligibility] = useState(null);
    const [checkingEligibility, setCheckingEligibility] = useState(false);

    // ESC + Android back cierran el modal
    useModalClose(onClose, true);

    useEffect(() => {
        let cancelled = false;
        verificationMine()
            .then(({ data }) => {
                if (cancelled) return;
                const list = data?.verifications || [];
                // Si el modal viene de un canal específico, filtramos la
                // última verificación PARA ESE CANAL. Si no, usamos la legacy.
                let latest = null;
                if (channelId) {
                    latest = list.find(v => v.channel && String(v.channel) === String(channelId)) || null;
                } else {
                    latest = list.find(v => !v.channel) || list[0] || null;
                }
                setCurrent(latest);
            })
            .catch(() => { if (!cancelled) setCurrent(null); })
            .finally(() => { if (!cancelled) setMineLoading(false); });
    }, [channelId]);

    // Cuando cambia el nivel o el canal, comprobamos eligibility con backend
    useEffect(() => {
        let cancelled = false;
        setCheckingEligibility(true);
        verificationEligibility(level, channelId)
            .then(({ data }) => { if (!cancelled) setEligibility(data); })
            .catch(() => { if (!cancelled) setEligibility(null); })
            .finally(() => { if (!cancelled) setCheckingEligibility(false); });
        return () => { cancelled = true; };
    }, [level, channelId]);

    const info = LEVEL_INFO[level];

    const handleFile = (field, file) => {
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            setError(L.fileTooBig);
            return;
        }
        setFiles(prev => ({ ...prev, [field]: file }));
        setError('');
    };

    const addSocialLink = () => setSocialLinks(prev => [...prev, '']);
    const changeSocialLink = (i, v) => setSocialLinks(prev => prev.map((x, idx) => idx === i ? v : x));
    const removeSocialLink = (i) => setSocialLinks(prev => prev.filter((_, idx) => idx !== i));

    const handleSubmit = async () => {
        setError('');

        // Validación de campos declarados
        if (!declared.legalName || !declared.documentNumber || !declared.country) {
            setError(L.errComplete);
            return;
        }
        if (level === 'business') {
            if (!declared.companyName || !declared.taxId || !declared.registryDate) {
                setError(L.errCompany);
                return;
            }
            const diffDays = (Date.now() - new Date(declared.registryDate).getTime()) / (1000 * 60 * 60 * 24);
            if (diffDays < 0) { setError(L.errFutureDate); return; }
            if (diffDays > 30) { setError(L.errOldRegistry); return; }
        }

        // Validar archivos requeridos
        const needed = info.required;
        for (const field of needed) {
            if (field === 'socialLinks') continue;
            if (!files[field]) {
                setError(`${L.errMissingFile}: ${field}`);
                return;
            }
        }
        if (level === 'creator') {
            const validLinks = socialLinks.filter(l => l.trim().length > 0);
            if (validLinks.length === 0) {
                setError(L.errSocialMin);
                return;
            }
        }

        // Construir FormData — backend extrae campos sueltos de req.body
        const fd = new FormData();
        fd.append('level', level);
        if (channelId) fd.append('channelId', String(channelId));
        Object.entries(declared).forEach(([k, v]) => {
            if (v) fd.append(k, v);
        });
        if (level === 'creator') {
            fd.append('socialLinks', JSON.stringify(socialLinks.filter(l => l.trim().length > 0)));
        }
        Object.entries(files).forEach(([k, f]) => {
            if (f) fd.append(k, f);
        });

        setSubmitting(true);
        try {
            await verificationSubmit(fd);
            // Recargar estado
            const { data } = await verificationMine();
            const list = data?.verifications || [];
            setCurrent(list[0] || null);
            // Si es nivel de pago (creator/business), abrir flujo de pagos.
            // El usuario sigue como "pending" hasta que admin apruebe; pero
            // el inicio del pago va asociado a la solicitud para que cuando
            // se apruebe ya esté la suscripción activa.
            if (level === 'creator' || level === 'business') {
                setShowPayments(true);
            }
        } catch (err) {
            setError(friendlyError(err, L.sendErrFallback));
        } finally {
            setSubmitting(false);
        }
    };

    // Si ya hay solicitud, mostrar estado en lugar de formulario
    if (!mineLoading && current && ['pending', 'info_requested'].includes(current.status)) {
        const s = getStatusInfo(current.status, L);
        return (
            <div className="vm-overlay" onClick={onClose}>
                <div className="vm-modal" onClick={e => e.stopPropagation()}>
                    <div className="vm-header">
                        <h3>{L.verifyInProgress}</h3>
                        <button className="vm-close" onClick={onClose}>✕</button>
                    </div>
                    <div className="vm-status-wrap">
                        <div className="vm-status-badge" style={{ background: s.color + '22', color: s.color }}>
                            {s.text}
                        </div>
                        <p>{L.levelRequested}: <strong>{LEVEL_INFO[current.level]?.icon} {LEVEL_INFO[current.level]?.label}</strong></p>
                        <p>{L.sentAt}: {new Date(current.submittedAt).toLocaleString()}</p>
                        {current.status === 'info_requested' && current.infoRequested && (
                            <div className="vm-info-requested">
                                <h4>{L.infoRequestedH}</h4>
                                <p>{current.infoRequested}</p>
                                <p className="vm-hint">{L.cancelAndResubmit}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Si ya tiene verificación aprobada Y NO está en modo "evolucionar",
    // mostramos status + botón Evolucionar (si hay nivel superior).
    // Cuando pulsa Evolucionar, levantamos `wantsUpgrade` y dejamos que
    // siga al form de abajo con el siguiente nivel preseleccionado.
    const canUpgrade = current?.status === 'approved'
        && (current.level === 'basic' || current.level === 'creator');
    if (!mineLoading && current?.status === 'approved' && !wantsUpgrade) {
        const nextLevel = current.level === 'basic' ? 'creator' : current.level === 'creator' ? 'business' : null;
        return (
            <div className="vm-overlay" onClick={onClose}>
                <div className="vm-modal" onClick={e => e.stopPropagation()}>
                    <div className="vm-header">
                        <h3>{L.alreadyVerified}</h3>
                        <button className="vm-close" onClick={onClose}>✕</button>
                    </div>
                    <div className="vm-status-wrap">
                        <div className="vm-status-badge" style={{ background: '#22c55e22', color: '#22c55e' }}>
                            ✓ {LEVEL_INFO[current.level]?.icon} {LEVEL_INFO[current.level]?.label}
                        </div>
                        <p>{L.approvedAt}: {new Date(current.reviewedAt || current.submittedAt).toLocaleDateString()}</p>
                        {current.expiresAt && (
                            <p>{L.renewalAt}: {new Date(current.expiresAt).toLocaleDateString()}</p>
                        )}
                        {canUpgrade && nextLevel && (
                            <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid #eee', textAlign: 'center' }}>
                                <p style={{ marginBottom: 12, color: '#666' }}>
                                    {L.wantUpgrade} <strong>{LEVEL_INFO[nextLevel]?.label}</strong>?
                                </p>
                                <button
                                    className="vm-btn-primary"
                                    onClick={() => { setLevel(nextLevel); setWantsUpgrade(true); }}
                                >
                                    {L.evolveBtn}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Formulario de solicitud
    return (
        <div className="vm-overlay" onClick={onClose}>
            <div className="vm-modal" onClick={e => e.stopPropagation()}>
                <div className="vm-header">
                    <h3>{L.title}</h3>
                    <button className="vm-close" onClick={onClose}>✕</button>
                </div>

                <div className="vm-body">
                    {current?.status === 'rejected' && (
                        <div className="vm-rejected-notice">
                            <strong>{L.rejectedHeader}</strong>
                            <p>{current.rejectionReason}</p>
                            <p className="vm-hint">{L.canResubmit}</p>
                        </div>
                    )}

                    {/* Selector de nivel */}
                    <div className="vm-level-grid">
                        {Object.entries(LEVEL_INFO).map(([k, v]) => (
                            <button
                                key={k}
                                className={`vm-level-card ${level === k ? 'is-selected' : ''}`}
                                onClick={() => setLevel(k)}
                            >
                                <div className="vm-level-icon">{v.icon}</div>
                                <div className="vm-level-label">{v.label}</div>
                                <div className="vm-level-price">{v.price}</div>
                                <div className="vm-level-sub">{v.subtitle}</div>
                            </button>
                        ))}
                    </div>

                    {/* Requisitos mínimos para el nivel seleccionado */}
                    <div className={`vm-eligibility ${eligibility?.eligible === false ? 'is-blocked' : (eligibility?.eligible ? 'is-ok' : '')}`}>
                        {checkingEligibility && (
                            <div className="vm-eligibility-loading">{L.checkingReqs}</div>
                        )}
                        {!checkingEligibility && eligibility && (
                            <>
                                <div className="vm-eligibility-head">
                                    <strong>{eligibility.eligible ? L.meetsReqs : L.notMeetsReqs}</strong>
                                </div>
                                {eligibility.requirements?.description && (
                                    <p className="vm-eligibility-desc">{eligibility.requirements.description}</p>
                                )}
                                {eligibility.reasons?.length > 0 && (
                                    <ul className="vm-eligibility-reasons">
                                        {eligibility.reasons.map((r, i) => <li key={i}>{r}</li>)}
                                    </ul>
                                )}
                            </>
                        )}
                    </div>

                    {/* Datos declarados */}
                    <section className="vm-section">
                        <h4>{L.personalDataH}</h4>
                        <input
                            className="vm-input"
                            placeholder={L.legalNamePh}
                            value={declared.legalName}
                            onChange={e => setDeclared(d => ({ ...d, legalName: e.target.value }))}
                        />
                        <input
                            className="vm-input"
                            placeholder={L.documentNumberPh}
                            value={declared.documentNumber}
                            onChange={e => setDeclared(d => ({ ...d, documentNumber: e.target.value }))}
                        />
                        <input
                            className="vm-input"
                            placeholder={L.countryPh}
                            value={declared.country}
                            onChange={e => setDeclared(d => ({ ...d, country: e.target.value }))}
                        />
                    </section>

                    {level === 'business' && (
                        <section className="vm-section">
                            <h4>{L.companyDataH}</h4>
                            <input
                                className="vm-input"
                                placeholder={L.companyNamePh}
                                value={declared.companyName}
                                onChange={e => setDeclared(d => ({ ...d, companyName: e.target.value }))}
                            />
                            <input
                                className="vm-input"
                                placeholder={L.taxIdPh}
                                value={declared.taxId}
                                onChange={e => setDeclared(d => ({ ...d, taxId: e.target.value }))}
                            />
                            <label className="vm-label">{L.registryDateLabel}</label>
                            <input
                                className="vm-input"
                                type="date"
                                value={declared.registryDate}
                                onChange={e => setDeclared(d => ({ ...d, registryDate: e.target.value }))}
                            />
                        </section>
                    )}

                    {/* Documentos de identidad (todos los niveles) */}
                    <section className="vm-section">
                        <h4>{L.personalIdentityH}</h4>
                        <FileInput
                            label={L.idFrontLabel}
                            hint={L.idFrontHint}
                            file={files.idFront}
                            onFile={f => handleFile('idFront', f)}
                        />
                        <FileInput
                            label={L.idBackLabel}
                            hint={L.idBackHint}
                            file={files.idBack}
                            onFile={f => handleFile('idBack', f)}
                        />
                        <FileInput
                            label={L.selfieLabel}
                            hint={L.selfieHint}
                            file={files.selfie}
                            onFile={f => handleFile('selfie', f)}
                        />
                    </section>

                    {level === 'creator' && (
                        <section className="vm-section">
                            <h4>{L.socialLinksH}</h4>
                            <p className="vm-hint">{L.socialLinksHint}</p>
                            {socialLinks.map((l, i) => (
                                <div key={i} className="vm-social-row">
                                    <input
                                        className="vm-input"
                                        placeholder={L.socialPlaceholder}
                                        value={l}
                                        onChange={e => changeSocialLink(i, e.target.value)}
                                    />
                                    <button className="vm-remove" onClick={() => removeSocialLink(i)}>✕</button>
                                </div>
                            ))}
                            <button className="vm-add-link" onClick={addSocialLink}>
                                {socialLinks.length === 0 ? L.addLink : L.addAnotherLink}
                            </button>
                        </section>
                    )}

                    {level === 'business' && (
                        <section className="vm-section">
                            <h4>{L.companyDocsH}</h4>
                            <FileInput
                                label={L.articlesLabel}
                                hint={L.articlesHint}
                                file={files.articles}
                                onFile={f => handleFile('articles', f)}
                            />
                            <FileInput
                                label={L.registryExtractLabel}
                                hint={L.registryExtractHint}
                                file={files.registryExtract}
                                onFile={f => handleFile('registryExtract', f)}
                            />
                            <FileInput
                                label={L.powerOfAttorneyLabel}
                                hint={L.powerOfAttorneyHint}
                                file={files.powerOfAttorney}
                                onFile={f => handleFile('powerOfAttorney', f)}
                            />
                        </section>
                    )}

                    {error && <div className="vm-error">{error}</div>}

                    <div className="vm-legal">{L.legalDisclaimer}</div>

                    <div className="vm-actions">
                        <button className="vm-btn-secondary" onClick={onClose}>{L.cancel}</button>
                        <button
                            className="vm-btn-primary"
                            onClick={handleSubmit}
                            disabled={submitting || (eligibility && eligibility.eligible === false)}
                            title={eligibility?.eligible === false ? L.notMeetsTitle : ''}
                        >
                            {submitting ? L.submitting : `${L.submit}${info.price !== 'Gratis' ? ' · ' + info.price : ''}`}
                        </button>
                    </div>
                </div>
            </div>
            {showPayments && (
                <PlansModal onClose={() => { setShowPayments(false); onClose(); }} />
            )}
        </div>
    );
}

function FileInput({ label, hint, file, onFile }) {
    return (
        <label className="vm-file-row">
            <div>
                <div className="vm-file-label">{label}</div>
                <div className="vm-file-hint">{hint}</div>
                {file && <div className="vm-file-name">📎 {file.name}</div>}
            </div>
            <div className="vm-file-btn">
                {file ? 'Cambiar' : 'Seleccionar'}
                <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={e => onFile(e.target.files?.[0])}
                />
            </div>
        </label>
    );
}
